import { Domain } from 'library/ts/model/enums/web/Domain';
import { SubscriberPackageKey } from 'library/ts/model/user/SubscriberPackageKey';
import { getSmartTagRuntimeVO } from 'tag/ts/smart_tag/utilities/Runtime';
import { FreewheelParameterType } from 'tag/ts/tag/ads/freewheel/enums/FreewheelParameterType';
import { FreewheelRequestType } from 'tag/ts/tag/ads/freewheel/enums/FreewheelRequestType';
import { IFreewheelRequestArgument } from 'tag/ts/tag/ads/freewheel/interfaces/IFreewheelRequestArgument';
import { FreewheelParameter } from 'tag/ts/tag/ads/freewheel/parameters/FreewheelParameter';
import { AbstractFreewheelRequest } from 'tag/ts/tag/ads/freewheel/requests/AbstractFreewheelRequest';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { FreewheelConstants } from 'tag/ts/tag/model/FreewheelConstants';
import { UserModel } from 'tag/ts/tag/model/vo/UserModel';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Creates and returns Freewheel IMA request
 */
export class FreewheelImaRequest extends AbstractFreewheelRequest {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'FreewheelImaRequest',
				type: FreewheelRequestType.IMA
			} as IFreewheelRequestArgument
		);

		const paramsVO = this.tag.params;
		const siteSessionData = this.tag.model.siteUtilities.siteSessionData;
		const apiMetadata = this.tag?.model?.apiMetadata;
		const constants = FreewheelConstants.getInstance();
		const fmsResponse = paramsVO?.AD?.FW?.FMS;
		const gdprResponse = paramsVO?.AD?.FW?.GDPR;
		const runtimeVO = getSmartTagRuntimeVO();
		const isPPlus = !!runtimeVO.top.parsed.host.includes( Domain.PPLUS );
		const coppa = ( !!isPPlus && !this.utils.Type.isEmpty( siteSessionData?.tfcd ) ) ? siteSessionData.tfcd.toString() : undefined;
		const referrer = runtimeVO.top.url.split( '?' )[ 0 ];
		const userModel = new UserModel( { tag: this.tag, paramsUser: paramsVO.USER } );
		const currentValidStatus = userModel.getCurrentValidStatus();
		const siteSectionId = siteSessionData?.csid?.toLowerCase() || this.createSiteSectionId();
		const tfcd = !this.utils.Type.isEmpty( siteSessionData?.tfcd ) ? siteSessionData.tfcd.toString() : undefined;
		const tve = currentValidStatus?.key === SubscriberPackageKey.TVE ? currentValidStatus?.value : undefined;
		const tsb = currentValidStatus?.key === SubscriberPackageKey.TSB ? currentValidStatus?.value : undefined;
		const sb = currentValidStatus?.key === SubscriberPackageKey.SB ? currentValidStatus?.value : undefined;
		const descUrl = !!isPPlus ? constants.descUrlPPlus : constants.descUrlCBS;
		const isLive = !!this.tag.model.contentUtilities.isLive( paramsVO, apiMetadata );
		[
			{ key: 'asnw', value: constants.assetNetworkId, type: FreewheelParameterType.GLOBAL },
			{ key: 'caid', value: apiMetadata?.contentId, type: FreewheelParameterType.GLOBAL },
			{ key: 'csid', value: siteSectionId, type: FreewheelParameterType.GLOBAL },
			{ key: 'flag', value: constants.capabilitiesFlag, type: FreewheelParameterType.GLOBAL },
			{ key: 'metr', value: constants.defaultCapabilities, type: FreewheelParameterType.GLOBAL },
			{ key: 'nw', value: constants.networkId, type: FreewheelParameterType.GLOBAL },
			{ key: 'prof', value: constants.playerProfile, type: FreewheelParameterType.GLOBAL },
			{ key: 'pvrn', value: constants.pageView, type: FreewheelParameterType.GLOBAL },
			{ key: 'resp', value: constants.response, type: FreewheelParameterType.GLOBAL },
			{ key: 'ssnw', value: constants.siteSectionNetworkId, type: FreewheelParameterType.GLOBAL },
			{ key: 'vprn', value: constants.videoPlayer, type: FreewheelParameterType.GLOBAL },
			{ key: '_fw_ae', value: paramsVO?.USER?.mvpdId, type: FreewheelParameterType.SITE },
			{ key: '_fw_continuous_play', value: ( !!isLive ? undefined : siteSessionData?.cpSession ), type: FreewheelParameterType.SITE },
			{ key: '_fw_coppa', value: coppa, type: FreewheelParameterType.SITE },
			{ key: '_fw_gdpr', value: gdprResponse?._fw_gdpr?.toString(), type: FreewheelParameterType.SITE },
			{ key: '_fw_gdpr_consent', value: gdprResponse?._fw_gdpr_consent?.toString(), type: FreewheelParameterType.SITE },
			{ key: '_fw_h_referer', value: referrer, type: FreewheelParameterType.SITE },
			{ key: '_fw_us_privacy', value: paramsVO?.USER?.usPrivacy, type: FreewheelParameterType.SITE },
			{ key: '_fw_vcid2', value: fmsResponse?._fw_vcid2, type: FreewheelParameterType.SITE },
			{ key: 'region', value: siteSessionData?.region, type: FreewheelParameterType.SITE },
			{ key: '_fw_is_lat', value: siteSessionData?._fw_is_lat, type: FreewheelParameterType.SITE },
			{ key: 'bundle', value: paramsVO?.USER?.bundleStatus, type: FreewheelParameterType.CUSTOM },
			{ key: 'cpPre', value: ( !!isLive ? undefined : siteSessionData?.cpPre ), type: FreewheelParameterType.CUSTOM },
			{ key: 'cpSession', value: ( !!isLive ? undefined : siteSessionData?.cpSession ), type: FreewheelParameterType.CUSTOM },
			{ key: 'description_url', value: descUrl, type: FreewheelParameterType.CUSTOM },
			{ key: 'ge', value: siteSessionData?.ge, type: FreewheelParameterType.CUSTOM },
			{ key: 'gr', value: siteSessionData?.gr, type: FreewheelParameterType.CUSTOM },
			{ key: 'host', value: siteSessionData?.host, type: FreewheelParameterType.CUSTOM },
			{ key: 'packageSource', value: paramsVO?.USER?.packageSource, type: FreewheelParameterType.CUSTOM },
			{ key: 'partner', value: siteSessionData?.partner, type: FreewheelParameterType.CUSTOM },
			{ key: 'ptype', value: siteSessionData?.ptype, type: FreewheelParameterType.CUSTOM },
			{ key: 'sb', value: sb, type: FreewheelParameterType.CUSTOM },
			{ key: 'tsb', value: tsb, type: FreewheelParameterType.CUSTOM },
			{ key: 'section', value: siteSessionData?.section, type: FreewheelParameterType.CUSTOM },
			{ key: 'session', value: siteSessionData?.session, type: FreewheelParameterType.CUSTOM },
			{ key: 'sub', value: siteSessionData?.sub, type: FreewheelParameterType.CUSTOM },
			{ key: 'subses', value: siteSessionData?.subses, type: FreewheelParameterType.CUSTOM },
			{ key: 'sz', value: constants.size, type: FreewheelParameterType.CUSTOM },
			{ key: 'tfcd', value: tfcd, type: FreewheelParameterType.CUSTOM },
			{ key: 'tve', value: tve, type: FreewheelParameterType.CUSTOM },
			{ key: 'user', value: paramsVO?.CONTEXT?.metadata?.userProfileCategory, type: FreewheelParameterType.CUSTOM },
			{ key: 'vguid', value: siteSessionData?.vguid, type: FreewheelParameterType.CUSTOM },
			{ key: '_fw_h_x_country', value: siteSessionData?.region, type: FreewheelParameterType.CUSTOM },
			{ key: '_fw_content_language', value: siteSessionData?.lang, type: FreewheelParameterType.CUSTOM },
			{ key: 'device_region_language', value: siteSessionData?.device_region_language, type: FreewheelParameterType.CUSTOM },
			{ key: 'language_selection', value: this.createLanguageSelection(), type: FreewheelParameterType.CUSTOM },
			{ key: 'endcard', value: siteSessionData?.endcard, type: FreewheelParameterType.CUSTOM },
			{ key: 'gpp', value: siteSessionData?.gpp, type: FreewheelParameterType.CUSTOM },
			{ key: 'gpp_sid', value: siteSessionData?.gpp_sid, type: FreewheelParameterType.CUSTOM },
			{ key: 'cadence', value: siteSessionData?.billing, type: FreewheelParameterType.CUSTOM },
			{ key: 'sublife', value: siteSessionData?.sublife, type: FreewheelParameterType.CUSTOM },
			{ key: 'packageType', value: siteSessionData?.packageType, type: FreewheelParameterType.CUSTOM },
			{ key: 'profile', value: siteSessionData?.profile?.toString(), type: FreewheelParameterType.CUSTOM },
			{ key: 'vendorUserType', value: siteSessionData?.vendorUserType, type: FreewheelParameterType.CUSTOM }
		].forEach( ( p ) => this.addParameter( new FreewheelParameter( p.key, p.value, p.type ) ) );

		// create FreewheelParameters from FMS and append them
		this.addFmsParameters( fmsResponse );
	}

	/**
	 * @description Returns request as a qualified ad call url string
	 * @return {string}
	 */
	createAdCallUrl(): string {
		const constants = FreewheelConstants.getInstance();
		const scheme = constants.scheme;
		const host = constants.hostIMA;

		// separate global and other parameters so url is correctly formatted:
		// https://docs.google.com/spreadsheets/d/17j5eNLIwssHBdbss_M7lW4rP5_7lbBMASnyOiKxd7L4/edit#gid=1189587938&range=A2
		const otherParameters = this.getParametersByType( [ FreewheelParameterType.SITE, FreewheelParameterType.CUSTOM ] );
		const globalParamsQueryString = this.getGlobalParameters().map( ( param ) => `${param.key}=${this.toUrlParam( param.value )}` ).join( '&' );
		const otherParametersQueryString = otherParameters.map( ( param ) => `${param.key}=${this.toUrlParam( param.value )}` ).join( '&' );

		const url = `${scheme}://${host}?${globalParamsQueryString};${otherParametersQueryString};`;
		this?.logger?.log( 'createAdCallUrl', 'url:', url );

		return url;
	}
}